/* Redux */
import { AnyAction, Dispatch } from 'redux';
import { tipsActions } from '../../../store/tips/actions';
import { premiumInfoActions } from '../../../store/premium/actions';

/* Interfaces */
import { Payload } from './Payload';
import {
  PaymentError,
  PaymentProcessor,
  WithModalHandler,
  WithPayload,
} from './PaymentProcessor';
import { PixPurchaseResponse } from './PurchaseResponse';
import { AnalyticsClient } from '../../../analytics/Client';
import { StoreType, StoreTypesReversed } from './store_types';
import { PixVerificationResponse } from '../../../api/payments/Pix/interfaces';
import { PurchaseStatus } from '../../Shops/shared/__providers__/PurchaseStatusProvider';
import { Shop } from '../../Shops/shared/interfaces';
import { TipProduct } from '../../Shops/Tip/interfaces/TipProduct';
import { Price } from './Price';
import { PremiumProduct } from '../../Shops/Premium/interfaces/PremiumProduct';

/* Services */
import {
  requestPremiumPixPayment,
  verifyPremiumPixPayment,
} from '../../../api/payments/Pix/premiumPayments';
import { requestTipPixPayment } from '../../../api/payments/Pix/tipPayments';

/* Toast */
import { toast } from 'react-toastify';

/* Translations */
import i18n from '../../../i18n';

export class PixPaymentProcessor
  extends PaymentProcessor
  implements WithPayload, WithModalHandler
{
  protected purchaseID: string | null = null;
  public payload: Payload;
  public isModalOpen: (opener: boolean) => void;
  public setPurchaseID: (purchaseID: string) => void;
  public setLinkAuthorize: (linkAuthorize: string) => void;

  constructor(
    payload: Payload,
    dispatcher: Dispatch<AnyAction>,
    loader: (load: boolean) => void,
    analyticsClient: AnalyticsClient,
    setPurchaseStatus: (status: PurchaseStatus) => void,
    setIsPendingModalOpen: (isPendingModalOpen: boolean) => void,
    setPurchaseID: (purchaseID: string) => void,
    setLinkAuthorize: (linkAuthorize: string) => void,
  ) {
    super(dispatcher, loader, analyticsClient, setPurchaseStatus);

    this.setPurchaseID = setPurchaseID;
    this.setLinkAuthorize = setLinkAuthorize;
    this.isModalOpen = setIsPendingModalOpen;
    this.payload = payload;
  }

  async processPayment(store: StoreType): Promise<PixPurchaseResponse | void> {
    this.loader(true);
    try {
      switch (store) {
        case StoreTypesReversed.Premium:
          const {
            premium_plan_purchase: {
              id: premiumID,
              link_authorize: premiumLinkAuthorize,
            },
          } = await requestPremiumPixPayment(this.payload);
          this.setPurchaseID(premiumID);
          this.setLinkAuthorize(premiumLinkAuthorize);

          /* Open new window with the QR code */
          let w = window.open(premiumLinkAuthorize, '_blank');
          if (w) {
            w.focus();
          }

          this.setPurchaseStatus(PurchaseStatus.PENDING);
          this.loader(false);

          break;

        case StoreTypesReversed.TipPurchase:
          const {
            purchase: {
              id: tipID,
              payment_provider: { link_authorize: tipLinkAuthorize },
            },
          } = await requestTipPixPayment(this.payload);
          this.setPurchaseID(tipID);
          this.setLinkAuthorize(tipLinkAuthorize);

          /* Open new window with the QR code */
          w = window.open(tipLinkAuthorize, '_blank');
          if (w) {
            w.focus();
          }

          this.setPurchaseStatus(PurchaseStatus.PENDING);
          this.loader(false);

          break;
      }
    } catch (error: unknown) {
      this.handleError(error as PaymentError);
    }
  }

  public async finishPayment(
    store: StoreType,
    purchaseID: string | null,
    product: Shop,
  ): Promise<any> {
    this.loader(true);
    try {
      const {
        purchase: { state },
      }: PixVerificationResponse = await verifyPremiumPixPayment(purchaseID);

      switch (state as PurchaseStatus) {
        case PurchaseStatus.PENDING:
          this.isModalOpen(true);
          break;
        //@ts-ignore
        case PurchaseStatus.COMPLETED:
          const tipsterInfo = product.tipsterInfo;

          switch (store) {
            case StoreTypesReversed.Premium:
              const premiumProduct = product.selectedProduct as PremiumProduct;

              this.analyticsClient.firebase.log('purchase', [
                {
                  transaction_id: this.purchaseID || '',
                  store: 'Pix',
                  type: 'premium',
                  currency: premiumProduct.priceWithDiscount.currency,
                  value: premiumProduct.priceWithDiscount
                    .buildPrice()
                    .toNumber(),
                  items: [
                    {
                      item_id: premiumProduct.plan.pricing_tier_code,
                      item_name: `Premium Tier ${premiumProduct.getPricingTierCodeNumber()}`,
                      item_list_name: 'Premium',
                    },
                  ],
                },
              ]);

              this.dispatcher(
                premiumInfoActions.publicPremiumInfoRequestAction(
                  tipsterInfo.id,
                ),
              );
              break;

            case StoreTypesReversed.TipPurchase:
              const tipProduct = product.selectedProduct as TipProduct;

              this.analyticsClient.firebase.log('purchase', [
                {
                  transaction_id: purchaseID || '',
                  store: 'Pix',
                  type: 'tip_coupon',
                  currency: tipProduct.tip.web_price.currency,
                  value: new Price(
                    tipProduct.tip.web_price_with_discount.amount,
                    tipProduct.tip.web_price_with_discount.currency,
                    tipProduct.tip.web_price_with_discount.decimals,
                  )
                    .buildPrice()
                    .toNumber(),
                  items: [
                    {
                      item_id: tipProduct.tip.pricing_tier_code,
                      item_name: tipProduct.tip.pricing_tier_code,
                      item_list_name: 'Tip',
                    },
                  ],
                },
              ]);

              this.dispatcher(
                tipsActions.tipProductRequestAction(tipProduct.tip.id),
              );
              break;
          }

          toast.success(`${i18n.t('translation:payment_success')}`);
          break;

        default:
          toast.error(i18n.t('translation:payment_error'));
      }
    } catch (error: unknown) {
      this.handleError(error as PaymentError);
    } finally {
      this.loader(false);
    }
  }

  public handleError(error: PaymentError): void {
    toast.error(i18n.t(error.detail || error.message));

    this.setPurchaseStatus(PurchaseStatus.FAILED);
    this.loader(false);
  }
}
